import React, { useState, useEffect, useRef, useCallback } from 'react';
import { X, Loader2, Database, User } from 'lucide-react';
import * as LucideIcons from 'lucide-react';
import SqlexecTable from './SqlexecTable';
import { useSisConfig } from '../../contexts/SisConfigUi';
import SqlexecSplitPanel from './SqlexecSplitPanel';
import { secureFetch } from '../../utils/authInterceptor';
import { compensarEscalaVh } from '../../utils/formHelpers';


const SqlexecPanel = ({ moduleComponentForm, onClose }) => {
  
  const { panel_ui, modales_ui } = useSisConfig();
  const [tabs, setTabs] = useState([]);
  const [activeTab, setActiveTab] = useState('');
  const [loading, setLoading] = useState(true);

  // 🔥 Estados para selección de fila
  //const [selectedParentId, setSelectedParentId] = useState(null);
  //const [selectedParentRow, setSelectedParentRow] = useState(null); // ← NUEVO

  const [selectedParents, setSelectedParents] = useState({});
  const [splitPanelOpen, setSplitPanelOpen]     = useState(false);
  const [splitPanelConfig, setSplitPanelConfig] = useState(null);

  const lastRefreshTime = useRef(Date.now());
  const lastFocusTime = useRef(Date.now());
  const isFirstLoad = useRef(true);
  const splitPanelOpenRef = useRef(false);

  // Función de carga de tabs
  const loadPanelConfig = useCallback(async (silent = false) => {
    try {
      const token = sessionStorage.getItem('mapasac_access_token');
      
      const response = await secureFetch(`/api/sqlexec/${moduleComponentForm}/read?_t=${Date.now()}`, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache'
        }
      });
      
      const json = await response.json();
      
      if (json?.success) {
        // 🔥 Validación defensiva: asegurar que sea array
        const rawTabs = json.data.sqlexec_tabs;
        const panelTabs = Array.isArray(rawTabs) ? rawTabs : [];
        
        // Log para debugging si viene mal
        if (rawTabs && !Array.isArray(rawTabs)) {
          console.warn('⚠️ sqlexec_tabs no es array:', typeof rawTabs, rawTabs);
        }
        
        setTabs(panelTabs);
        
        setActiveTab(current => {
          if (isFirstLoad.current && panelTabs.length > 0) {
            isFirstLoad.current = false;
            const defaultTab = panelTabs.find(t => t.default);
            return defaultTab?.id || panelTabs[0]?.id || '';
          }
          
          const stillExists = panelTabs.find(t => t.id === current);
          if (stillExists) return current;
          
          if (panelTabs.length > 0) {
            const defaultTab = panelTabs.find(t => t.default);
            return defaultTab?.id || panelTabs[0]?.id || '';
          }
          
          return '';
        });
        
        lastRefreshTime.current = Date.now();
      }
    } catch (error) {
      console.error('Error cargando panel:', error);
    } finally {
      setLoading(false);
    }
  }, [moduleComponentForm]);

  /**
  * 🔥 Manejar selección de fila en tab principal
  */
  const handleRowSelect = useCallback((tabId, row) => {
    if (!row) return;

    setSelectedParents(prev => ({
      ...prev,
      [tabId]: {
        id: row[Object.keys(row)[0]], // o row.id si lo tienes normalizado
        row
      }
    }));

    // 🔥 Solo si splitpanel está abierto
    if (splitPanelOpenRef.current) {
      setSplitPanelConfig(prev => ({
        ...(prev || {}),
        selectedId: row[prev?.splitKey] ?? row[Object.keys(row)[0]],
        selectedRow: row
      }));
    }
  }, []);

  const rowSelectCallbacks = useRef({});

  const getRowSelectCallback = useCallback((tabId) => {
    if (!rowSelectCallbacks.current[tabId]) {
      rowSelectCallbacks.current[tabId] = (row) => handleRowSelect(tabId, row);
    }
    return rowSelectCallbacks.current[tabId];
  }, [handleRowSelect]);


  // Carga inicial
  useEffect(() => {
    if (moduleComponentForm) {
      loadPanelConfig(true);
    }
  }, [moduleComponentForm, loadPanelConfig]);

  // 🔥 Sincronizar ref con estado splitPanelOpen
  useEffect(() => {
    splitPanelOpenRef.current = splitPanelOpen;
  }, [splitPanelOpen]);

  useEffect(() => {
    const handler = (e) => {
      const { componentForm, selectedId, selectedRow, detailForm, splitKey, splitRatio, detailOnly } = e.detail;
      //setSplitPanelConfig({ componentForm, selectedId, selectedRow, detailForm, splitKey, splitRatio, detailOnly });
      //setSplitPanelOpen(prev => !prev);

      // 🔥 Toggle — si está abierto cerrar y limpiar, si está cerrado abrir
      if (splitPanelOpenRef.current) {
        setSplitPanelOpen(false);
        setSplitPanelConfig(null);
        window.dispatchEvent(new CustomEvent('sqlexec:splitpanel:closed', {
          detail: { componentForm }
        }));
      } else {
        setSplitPanelConfig({ componentForm, selectedId, selectedRow, detailForm, splitKey, splitRatio, detailOnly });
        setSplitPanelOpen(true);
      }
    };
    window.addEventListener('sqlexec:splitpanel', handler);
    return () => window.removeEventListener('sqlexec:splitpanel', handler);
  }, []);

  // 🔥 Limpiar splitpanel al cambiar de tab
  useEffect(() => {
    setSplitPanelOpen(false);
    setSplitPanelConfig(null);
    if (splitPanelConfig?.componentForm) {
      window.dispatchEvent(new CustomEvent('sqlexec:splitpanel:closed', {
        detail: { componentForm: splitPanelConfig.componentForm }
      }));
    }
    window.dispatchEvent(new CustomEvent('sqlexec:filtros:cerrar'));
  }, [activeTab]);

  // Auto-refresh al recuperar foco
  useEffect(() => {
    const handleFocus = async () => {
      const now = Date.now();
      const inactiveSeconds = (now - lastFocusTime.current) / 1000;
      
      if (inactiveSeconds > 30) {
        await loadPanelConfig(true);
      }
      
      lastFocusTime.current = now;
    };

    const handleBlur = () => {
      lastFocusTime.current = Date.now();
    };

    window.addEventListener('focus', handleFocus);
    window.addEventListener('blur', handleBlur);

    return () => {
      window.removeEventListener('focus', handleFocus);
      window.removeEventListener('blur', handleBlur);
    };
  }, [loadPanelConfig]);

  const getIcon = (iconName) => {
    if (!iconName) return null;
    const Icon = LucideIcons[iconName];
    return Icon ? <Icon size={20} /> : null;
  };

  if (loading) {
    return (
      <div className="fixed inset-0 bg-white z-50 flex items-center justify-center">
        <Loader2 className="animate-spin text-gray-900" size={48} />
      </div>
    );
  }

  if (tabs.length === 0) {
    return (
      <div className="fixed inset-0 bg-white z-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-lg text-gray-800">
            No hay tabs configurados para este módulo
          </p>
          <button
            onClick={onClose}
            className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Cerrar
          </button>
        </div>
      </div>
    );
  }
  
  // Detectar modo modal flotante
  const isModalMode = tabs.some(t => t.modal === true);
  const modalConfig = tabs.find(t => t.modal === true);

  if (isModalMode && modalConfig) {
    return (
      // ← Solo este wrapper cambia — flotante con overlay
      <div
        className="fixed inset-0 z-50 flex items-center justify-center"
        style={{
          //backgroundColor: modales_ui?.overlay?.backgroundColor || 'rgba(0,0,0,0.6)',
          backdropFilter: `blur(${modales_ui?.overlay?.backdropBlur || '4px'})`
        }}
      >
        <div style={{
          width:        compensarEscalaVh(modalConfig.modal_width  || '75vw'),
          height:       compensarEscalaVh(modalConfig.modal_height || '85vh'),
          borderRadius: modales_ui?.container?.borderRadius || '12px',
          boxShadow:    modales_ui?.container?.boxShadow    || '0 25px 50px -12px rgba(0,0,0,0.25)',
          overflow:     'hidden',
          display:      'flex',
          flexDirection: 'column'
        }}>

          {/* ← TODO LO DEMÁS IDÉNTICO AL RETURN NORMAL */}
          <div className="bg-white z-50 flex flex-col" style={{ flex: 1, minHeight: 0 }}>
            <div className="h-full flex flex-col">
              
              {/* Header idéntico */}
              <div style={{
                borderBottom:    panel_ui?.header?.borderBottom    || '1px solid #e5e7eb',
                backgroundColor: panel_ui?.header?.backgroundColor || '#fafafa'
              }}>
                <div className="flex items-center justify-between" style={{
                  paddingLeft:   panel_ui?.header?.padding?.x || '1.5rem',
                  paddingRight:  panel_ui?.header?.padding?.x || '1.5rem',
                  paddingTop:    panel_ui?.header?.padding?.y || '0.875rem',
                  paddingBottom: panel_ui?.header?.padding?.y || '0.875rem'
                }}>
                  {/* Tab activo — label del modalConfig */}
                  <div style={{
                    borderBottom:    panel_ui?.tabs?.container?.borderBottom || 'none',
                    paddingLeft:     panel_ui?.tabs?.container?.padding?.x   || '0.5rem',
                    paddingRight:    panel_ui?.tabs?.container?.padding?.x   || '0.5rem',
                    paddingTop:      panel_ui?.tabs?.container?.padding?.y   || '0.5rem',
                    paddingBottom:   panel_ui?.tabs?.container?.padding?.y   || '0.5rem',
                    backgroundColor: panel_ui?.tabs?.container?.backgroundColor || '#fafafa'
                  }}>
                    <div className="flex">
                      <button
                        className="flex items-center transition-all"
                        style={{
                          paddingLeft:     panel_ui?.tabs?.tab?.padding?.x      || '1.25rem',
                          paddingRight:    panel_ui?.tabs?.tab?.padding?.x      || '1.25rem',
                          paddingTop:      panel_ui?.tabs?.tab?.padding?.y      || '0.5rem',
                          paddingBottom:   panel_ui?.tabs?.tab?.padding?.y      || '0.5rem',
                          fontSize:        panel_ui?.tabs?.tab?.fontSize         || '0.875rem',
                          fontFamily:      panel_ui?.tabs?.tab?.fontFamily       || 'inherit',
                          fontWeight:      panel_ui?.tabs?.active?.fontWeight    || '600',
                          borderBottom:    `${panel_ui?.tabs?.tab?.borderBottomWidth || '3px'} solid ${panel_ui?.tabs?.active?.borderColor || '#3b82f6'}`,
                          borderRadius:    panel_ui?.tabs?.tab?.borderRadius     || '8px 8px 0 0',
                          color:           panel_ui?.tabs?.active?.textColor     || '#3b82f6',
                          backgroundColor: panel_ui?.tabs?.active?.backgroundColor || '#ffffff',
                          boxShadow:       panel_ui?.tabs?.active?.boxShadow     || '0 2px 6px rgba(0,0,0,0.08)',
                          gap:             panel_ui?.tabs?.tab?.iconGap          || '0.5rem',
                          cursor:          'default',
                          outline:         'none'
                        }}
                      >
                        {getIcon(modalConfig.icon)}
                        <span style={{ fontFamily: 'inherit' }}>{modalConfig.label}</span>
                      </button>
                    </div>
                  </div>

                  {/* Badge usuario + botón cerrar — idéntico */}
                  <div className="flex items-center" style={{ gap: '1rem' }}>
                    {(() => {
                      const badgeConfig = panel_ui?.userBadge || {};
                      if (!badgeConfig.show) return null;
                      try {
                        const session = JSON.parse(sessionStorage.getItem('mapasac_session'));
                        const nombre = session?.user?.login || '';
                        const permisos = session?.rbac?.permisos || [];
                        const permiso = permisos.find(p => p.componente_form === modalConfig.component_form);
                        const nivel = permiso?.acciones || 'read';
                        const nivelConfig = badgeConfig.niveles?.[nivel] || badgeConfig.niveles?.read || {};
                        const badge = badgeConfig.badge || {};
                        return (
                          <div className="flex items-center" style={{ gap: '0.5rem' }}>
                            <div className="flex items-center" style={{ gap: '0.4rem' }}>
                              <User size={badgeConfig.iconSize || 14} style={{ color: badgeConfig.iconColor || '#6b7280' }} />
                              <span style={{
                                fontSize:   badgeConfig.fontSize   || '0.60rem',
                                fontWeight: badgeConfig.fontWeight || '400',
                                color:      badgeConfig.textColor  || '#374151'
                              }}>{nombre}</span>
                            </div>
                            <span style={{ color: badgeConfig.separatorColor || '#d1d5db' }}>|</span>
                            <span style={{
                              fontSize:        badge.fontSize        || '0.60rem',
                              fontWeight:      badge.fontWeight      || '400',
                              paddingLeft:     badge.paddingX        || '8px',
                              paddingRight:    badge.paddingX        || '8px',
                              paddingTop:      badge.paddingY        || '2px',
                              paddingBottom:   badge.paddingY        || '2px',
                              borderRadius:    badge.borderRadius    || '9999px',
                              backgroundColor: nivelConfig.bg,
                              color:           nivelConfig.text,
                              border:          `1px solid ${nivelConfig.border}`,
                              textTransform:   'uppercase',
                              letterSpacing:   badge.letterSpacing   || '0.05em'
                            }}>{nivel}</span>
                          </div>
                        );
                      } catch { return null; }
                    })()}
                    <button
                      onClick={onClose}
                      className="rounded transition-all flex items-center justify-center"
                      style={{
                        padding:            panel_ui?.closeButton?.padding            || '0.5rem',
                        borderRadius:       panel_ui?.closeButton?.borderRadius       || '8px',
                        transitionDuration: panel_ui?.closeButton?.transitionDuration || '200ms',
                        backgroundColor:    'transparent',
                        border:             'none',
                        cursor:             'pointer'
                      }}
                      onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = panel_ui?.closeButton?.hoverBackgroundColor || '#e5e7eb'; }}
                      onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
                      title="Cerrar Panel"
                    >
                      <X
                        size={parseInt(panel_ui?.closeButton?.iconSize) || 20}
                        style={{ color: panel_ui?.closeButton?.iconColor || '#6b7280', transition: 'color 200ms' }}
                      />
                    </button>
                  </div>
                </div>
              </div>

              {/* SqlexecTable — idéntico al tab principal */}
              <div className="flex-1 flex flex-col overflow-hidden">
                <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
                  <SqlexecTable
                    componentForm={modalConfig.component_form}
                    parentKey={null}
                    parentValue={null}
                    parentRow={null}
                    onRowSelect={null}
                  />
                </div>
              </div>

            </div>
          </div>

        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-white z-50 flex flex-col">
      <div className="h-full flex flex-col">
        <div 
          style={{
            borderBottom: panel_ui?.header?.borderBottom || '1px solid #e5e7eb',
            backgroundColor: panel_ui?.header?.backgroundColor || '#fafafa'
          }}
        >
          <div 
            className="flex items-center justify-between"
            style={{
              paddingLeft: panel_ui?.header?.padding?.x || '1.5rem',
              paddingRight: panel_ui?.header?.padding?.x || '1.5rem',
              paddingTop: panel_ui?.header?.padding?.y || '0.875rem',
              paddingBottom: panel_ui?.header?.padding?.y || '0.875rem'
            }}
          >
            {/* Tabs */}
            <div 
              style={{
                borderBottom: panel_ui?.tabs?.container?.borderBottom || 'none',
                paddingLeft: panel_ui?.tabs?.container?.padding?.x || '0.5rem',
                paddingRight: panel_ui?.tabs?.container?.padding?.x || '0.5rem',
                paddingTop: panel_ui?.tabs?.container?.padding?.y || '0.5rem',
                paddingBottom: panel_ui?.tabs?.container?.padding?.y || '0.5rem',
                backgroundColor: panel_ui?.tabs?.container?.backgroundColor || '#fafafa'
              }}
            >
              <div 
                className="flex"
                style={{
                  gap: panel_ui?.tabs?.container?.gap || '0.25rem'
                }}
              >
              
              {tabs
                .filter(tab => tab.active !== false)
                .map((tab) => {
                  const isActive = activeTab === tab.id;
                  const tabConfig = panel_ui?.tabs?.tab || {};

                  return (
                    <button
                      key={tab.id}
                      onClick={() => setActiveTab(tab.id)}
                      className="flex items-center transition-all"
                      style={{
                        paddingLeft: tabConfig.padding?.x || '1.25rem',
                        paddingRight: tabConfig.padding?.x || '1.25rem',
                        paddingTop: tabConfig.padding?.y || '0.5rem',
                        paddingBottom: tabConfig.padding?.y || '0.5rem',
                        fontSize: tabConfig.fontSize || '0.875rem',
                        fontFamily: tabConfig.fontFamily || 'inherit',
                        fontWeight: isActive 
                          ? (panel_ui?.tabs?.active?.fontWeight || '600') 
                          : (panel_ui?.tabs?.inactive?.fontWeight || '500'),
                        borderBottom: isActive 
                          ? `${tabConfig.borderBottomWidth || '3px'} solid ${panel_ui?.tabs?.active?.borderColor || '#3b82f6'}`
                          : `${tabConfig.borderBottomWidth || '3px'} solid transparent`,
                        borderRadius: tabConfig.borderRadius || '8px 8px 0 0',
                        color: isActive 
                          ? (panel_ui?.tabs?.active?.textColor || '#3b82f6') 
                          : (panel_ui?.tabs?.inactive?.textColor || '#6b7280'),
                        backgroundColor: isActive 
                          ? (panel_ui?.tabs?.active?.backgroundColor || '#ffffff') 
                          : (panel_ui?.tabs?.inactive?.backgroundColor || 'transparent'),
                        boxShadow: isActive 
                          ? (panel_ui?.tabs?.active?.boxShadow || '0 2px 6px rgba(0, 0, 0, 0.08)') 
                          : 'none',
                        transitionDuration: tabConfig.transitionDuration || '200ms',
                        transitionProperty: 'all',
                        gap: tabConfig.iconGap || '0.5rem',
                        cursor: 'pointer',
                        outline: 'none'
                      }}
                      onMouseEnter={(e) => {
                        if (!isActive) {
                          const button = e.currentTarget;
                          button.style.color = panel_ui?.tabs?.inactive?.hoverTextColor || '#111827';
                          button.style.backgroundColor = panel_ui?.tabs?.inactive?.hoverBackgroundColor || '#f3f4f6';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (!isActive) {
                          const button = e.currentTarget;
                          button.style.color = panel_ui?.tabs?.inactive?.textColor || '#6b7280';
                          button.style.backgroundColor = panel_ui?.tabs?.inactive?.backgroundColor || 'transparent';
                        }
                      }}
                      title={tab.description || tab.label}
                    >
                      {getIcon(tab.icon)}
                      <span style={{ fontFamily: 'inherit' }}>{tab.label}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* ✅ BADGE USUARIO + BOTÓN CERRAR */}
            <div className="flex items-center" style={{ gap: '1rem' }}>

              {/* Badge usuario/permiso — 100% desde panel_ui.userBadge */}
              {(() => {
                const badgeConfig = panel_ui?.userBadge || {};
                if (!badgeConfig.show) return null;

                try {
                  const session = JSON.parse(sessionStorage.getItem('mapasac_session'));
                  const nombre = session?.user?.login || '';
                  const permisos = session?.rbac?.permisos || [];

                  // Permiso del tab activo
                  const tabActivo = tabs.find(t => t.default === true) || tabs[0];
                  const componentForm = tabActivo?.component_form || '';
                  const permiso = permisos.find(p => p.componente_form === componentForm);
                  const nivel = permiso?.acciones || 'read';
                  const nivelConfig = badgeConfig.niveles?.[nivel] || badgeConfig.niveles?.read || {};
                  const badge = badgeConfig.badge || {};

                  return (
                    <div className="flex items-center" style={{ gap: '0.5rem' }}>
                      {/* Nombre usuario */}
                      <div className="flex items-center" style={{ gap: '0.4rem' }}>
                        <User 
                          size={badgeConfig.iconSize || 14} 
                          style={{ color: badgeConfig.iconColor || '#6b7280' }} 
                        />
                        <span style={{
                          fontSize: badgeConfig.fontSize || '0.60rem',
                          fontWeight: badgeConfig.fontWeight || '400',
                          color: badgeConfig.textColor || '#374151'
                        }}>
                          {nombre}
                        </span>
                      </div>

                      {/* Separador */}
                      <span style={{ color: badgeConfig.separatorColor || '#d1d5db' }}>|</span>

                      {/* Badge nivel */}
                      <span style={{
                        fontSize: badge.fontSize || '0.60rem',
                        fontWeight: badge.fontWeight || '400',
                        paddingLeft: badge.paddingX || '8px',
                        paddingRight: badge.paddingX || '8px',
                        paddingTop: badge.paddingY || '2px',
                        paddingBottom: badge.paddingY || '2px',
                        borderRadius: badge.borderRadius || '9999px',
                        backgroundColor: nivelConfig.bg,
                        color: nivelConfig.text,
                        border: `1px solid ${nivelConfig.border}`,
                        textTransform: 'uppercase',
                        letterSpacing: badge.letterSpacing || '0.05em'
                      }}>
                        {nivel}
                      </span>
                    </div>
                  );
                } catch { return null; }
              })()}
              {/* Botón cerrar */}
              <button
                onClick={onClose}
                className="rounded transition-all flex items-center justify-center"
                style={{
                  padding: panel_ui?.closeButton?.padding || '0.5rem',
                  borderRadius: panel_ui?.closeButton?.borderRadius || '8px',
                  transitionDuration: panel_ui?.closeButton?.transitionDuration || '200ms',
                  backgroundColor: 'transparent',
                  border: 'none',
                  cursor: 'pointer'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = panel_ui?.closeButton?.hoverBackgroundColor || '#e5e7eb';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                }}
                title="Cerrar Panel"
              >
                <X 
                  size={parseInt(panel_ui?.closeButton?.iconSize) || 20} 
                  style={{ 
                    color: panel_ui?.closeButton?.iconColor || '#6b7280',
                    transition: 'color 200ms'
                  }}
                />
              </button>
            </div>
          </div>
        </div>
        
        {/* Contenido */}
        <div style={{display: 'flex', flexDirection: 'column', overflow: 'hidden', 
                    flex: splitPanelOpen ? (splitPanelConfig?.splitRatio?.master || 60) : 1}}>
          {tabs.map((tab) => {
            const isVisible = activeTab === tab.id;
            const isMainTab = tab.default === true;
            const isChildTab = tab.filtered === true;
            const isOrphanTab = !tab.default && !tab.filtered;

            // 🔥 Resolver contexto del padre SOLO si aplica
            const parentCtx = tab.parentTab ? selectedParents[tab.parentTab] : null;

            // ==========================
            // TAB PRINCIPAL
            // ==========================
            if (isMainTab) {
              return (
                <div 
                  key={tab.id} 
                  style={{ 
                    display: isVisible ? 'flex' : 'none',
                    flexDirection: 'column', 
                    flex: 1, 
                    minHeight: 0 
                  }}
                >
                  <SqlexecTable
                    componentForm={tab.component_form}
                    parentKey={null}
                    parentValue={null}
                    parentRow={null}
                    onRowSelect={getRowSelectCallback(tab.id)}
                    showFooter={splitPanelOpen ? false : undefined}
                  />
                </div>
              );
            }

            // ==========================
            // TAB HIJO
            // ==========================
            if (isChildTab) {
              if (!parentCtx) {
                const parentTabConfig = tabs.find(t => t.id === tab.parentTab);
                const labelLower = parentTabConfig?.label?.toLowerCase() || 'registro';                
                return (
                  <div 
                    key={tab.id}
                    style={{ display: isVisible ? 'flex' : 'none' }}
                    className="mt-8 flex-col items-center justify-center gap-2 text-xs"
                  >
                    <Database size={18} className="text-gray-400" />
                    <span className="text-gray-500">
                      Selecciona un registro del Tab : {labelLower}
                    </span>
                  </div>
                );
              }

              const esAlguienPadre = tabs.some(t => t.parentTab === tab.id);

              return (
                <div 
                  key={tab.id} 
                  style={{ 
                    display: isVisible ? 'flex' : 'none',
                    flexDirection: 'column', 
                    flex: 1, 
                    minHeight: 0 
                  }}
                >
                  <SqlexecTable
                    key={`${tab.id}-${parentCtx.id}`}
                    componentForm={tab.component_form}
                    parentKey={tab.parentKey}
                    parentValue={parentCtx.id}
                    parentRow={parentCtx.row}
                    onRowSelect={esAlguienPadre ? getRowSelectCallback(tab.id) : null}
                    showFooter={splitPanelOpen ? false : undefined}
                  />
                </div>
              );
            }

            // ==========================
            // TAB HUÉRFANO
            // ==========================
            if (isOrphanTab) {
              return (
                <div 
                  key={tab.id} 
                  style={{ 
                    display: isVisible ? 'flex' : 'none',
                    flexDirection: 'column', 
                    flex: 1, 
                    minHeight: 0 
                  }}
                >
                  <SqlexecTable
                    componentForm={tab.component_form}
                    parentKey={null}
                    parentValue={null}
                    parentRow={null}
                    onRowSelect={getRowSelectCallback(tab.id)}
                    showFooter={splitPanelOpen ? false : undefined}
                  />
                </div>
              );
            }

            return null;
          })}
        </div>

        {/* SPLITPANEL DINÁMICO — Ver Detalles */}
        {splitPanelConfig && (
          <div style={{ 
            display:       splitPanelOpen ? 'flex' : 'none', 
            flex:          splitPanelOpen ? (splitPanelConfig?.splitRatio?.detail || 40) : 0,
            flexDirection: panel_ui?.splitPanel?.wrapper?.flexDirection || 'column',
            minHeight:     panel_ui?.splitPanel?.wrapper?.minHeight     || 0,
            overflow:      panel_ui?.splitPanel?.wrapper?.overflow      || 'hidden',
            transition:    panel_ui?.splitPanel?.wrapper?.transition    || 'opacity 150ms ease'
          }}>
            <SqlexecSplitPanel
              componentForm={splitPanelConfig.componentForm}
              detailForm={splitPanelConfig.detailForm}
              splitKey={splitPanelConfig.splitKey}
              selectedId={splitPanelConfig.selectedId}
              selectedRow={splitPanelConfig.selectedRow}
              masterFlex={splitPanelConfig.splitRatio?.master || 60}
              detailFlex={splitPanelConfig.splitRatio?.detail || 40}
              detailOnly={splitPanelConfig.detailOnly}
            />
          </div>
        )}

      </div>
    </div>
  );
};

export default SqlexecPanel;

// Mejoras
//SqlexecPanel
// ├─ PanelShell
// │   ├─ PanelHeader
// │   │   ├─ PanelTabs
// │   │   │   └─ TabButton
// │   │   └─ PanelCloseButton
// │   └─ PanelContent
// │       └─ SqlexecTable
// Calidad arquitectónica, Efectividad funcional, Flexibilidad / escalabilidad, 
// Performance / costo de requests, Mantenibilidad, Nivel de primero mundo. 360 lineas el original